import React from 'react'
import { Box, Tooltip, Typography, makeStyles, Theme } from '@material-ui/core'
import StarIcon from '@material-ui/icons/Star'
import RemoveIcon from '@material-ui/icons/Remove'

import { OccupancyLease } from './types'
import {
    LIFESTYLE_COLUMNS,
    lifestyleFieldValue,
    lifestyleImportance,
    genderCellLabel,
    petCellLabel,
} from './occupancyLifestyle'

const useStyles = makeStyles((theme: Theme) => ({
    strip: {
        display: 'grid',
        gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
        gap: theme.spacing(0.75),
        flex: 1,
        minWidth: 0,
        alignItems: 'start',
    },
    cell: {
        minWidth: 0,
    },
    label: {
        display: 'block',
        fontSize: '0.5625rem',
        fontWeight: 700,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        color: theme.palette.text.secondary,
        lineHeight: 1.2,
        marginBottom: 2,
    },
    valueRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        minWidth: 0,
    },
    value: {
        fontSize: '0.75rem',
        fontWeight: 600,
        lineHeight: 1.2,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    none: {
        color: theme.palette.text.secondary,
        fontWeight: 500,
    },
    mustIcon: {
        fontSize: 12,
        color: '#c2410c',
        flexShrink: 0,
    },
    noneIcon: {
        fontSize: 12,
        color: theme.palette.text.secondary,
        flexShrink: 0,
    },
    incomplete: {
        fontSize: '0.75rem',
        fontStyle: 'italic',
        color: theme.palette.text.secondary,
        paddingTop: 12,
    },
}))

export function OccupancyLifestyleStrip({ lease }: { lease: OccupancyLease }) {
    const classes = useStyles()

    if (!lease.lifestyle_complete) {
        return (
            <Typography className={classes.incomplete}>
                Lifestyle incomplete
            </Typography>
        )
    }

    return (
        <Box className={classes.strip}>
            {LIFESTYLE_COLUMNS.map((column) => {
                const importance = lifestyleImportance(lease, column.id)
                const value = lifestyleFieldValue(lease, column.id)
                const weightLabel =
                    importance === 'must'
                        ? 'Must'
                        : importance === 'none'
                        ? 'No preference'
                        : 'Prefer'
                return (
                    <Box key={column.id} className={classes.cell}>
                        <Typography className={classes.label}>
                            {column.label}
                        </Typography>
                        <Tooltip
                            title={`${column.label}: ${value} · ${weightLabel}`}
                        >
                            <Box className={classes.valueRow}>
                                <Typography
                                    className={`${classes.value} ${
                                        importance === 'none'
                                            ? classes.none
                                            : ''
                                    }`}
                                >
                                    {value}
                                </Typography>
                                {importance === 'must' && (
                                    <StarIcon className={classes.mustIcon} />
                                )}
                                {importance === 'none' && (
                                    <RemoveIcon className={classes.noneIcon} />
                                )}
                            </Box>
                        </Tooltip>
                    </Box>
                )
            })}
            <Box className={classes.cell}>
                <Typography className={classes.label}>Gender</Typography>
                <Typography className={classes.value}>
                    {genderCellLabel(lease)}
                </Typography>
            </Box>
            <Box className={classes.cell}>
                <Typography className={classes.label}>Pets</Typography>
                <Typography className={classes.value}>
                    {petCellLabel(lease)}
                </Typography>
            </Box>
        </Box>
    )
}
